<template>
  <div class="editor">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive, focused }">
      <div class="menubar is-hidden" :class="{ 'is-focused': focused && editable }">
        <button class="menubar__button" @click.stop="commands.undo">
          <v-icon>mdi-undo</v-icon>
        </button>

        <button class="menubar__button" @click.stop="commands.redo">
          <v-icon>mdi-redo</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bold() }"
          @click.stop="commands.bold"
        >
          <v-icon>mdi-format-bold</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.italic() }"
          @click.stop="commands.italic"
        >
          <v-icon>mdi-format-italic</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.strike() }"
          @click.stop="commands.strike"
        >
          <v-icon>mdi-format-strikethrough-variant</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.underline() }"
          @click.stop="commands.underline"
        >
          <v-icon>mdi-format-underline</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.code() }"
          @click.stop="commands.code"
        >
          <v-icon>mdi-xml</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.paragraph() }"
          @click.stop="commands.paragraph"
        >
          <v-icon>mdi-format-paragraph</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 1 }) }"
          @click.stop="commands.heading({ level: 1 })"
        >
          H1
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 2 }) }"
          @click.stop="commands.heading({ level: 2 })"
        >
          H2
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.heading({ level: 3 }) }"
          @click.stop="commands.heading({ level: 3 })"
        >
          H3
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.bullet_list() }"
          @click.stop="commands.bullet_list"
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.ordered_list() }"
          @click.stop="commands.ordered_list"
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.blockquote() }"
          @click.stop="commands.blockquote"
        >
          <v-icon>mdi-format-quote-close</v-icon>
        </button>

        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.code_block() }"
          @click.stop="commands.code_block"
        >
          <v-icon>mdi-code-braces-box</v-icon>
        </button>
        <button
          class="menubar__button"
          :class="{ 'is-active': isActive.image() }"
          @click.stop="showImagePrompt(commands.image)"
        >
          <v-icon>mdi-image</v-icon>
        </button>
        <button
          class="menubar__button"
          @click="commands.createTable({ rowsCount: 3, colsCount: 3, withHeaderRow: false })"
        >
          <v-icon>mdi-table-large-plus</v-icon>
        </button>

        <span v-if="isActive.table()">
          <button class="menubar__button" @click="commands.deleteTable">
            <v-icon>mdi-table-large-remove</v-icon>
          </button>
          <button class="menubar__button" @click="commands.addColumnBefore">
            <v-icon>mdi-table-column-plus-before</v-icon>
          </button>
          <button class="menubar__button" @click="commands.addColumnAfter">
            <v-icon>mdi-table-column-plus-after</v-icon>
          </button>
          <button class="menubar__button" @click="commands.deleteColumn">
            <v-icon>mdi-table-column-remove</v-icon>
          </button>
          <button class="menubar__button" @click="commands.addRowBefore">
            <v-icon>mdi-table-row-plus-before</v-icon>
          </button>
          <button class="menubar__button" @click="commands.addRowAfter">
            <v-icon>mdi-table-row-plus-after</v-icon>
          </button>
          <button class="menubar__button" @click="commands.deleteRow">
            <v-icon>mdi-table-row-remove</v-icon>
          </button>
          <button class="menubar__button" @click="commands.toggleCellMerge">
            <v-icon>mdi-table-border</v-icon>
          </button>
        </span>
      </div>
    </editor-menu-bar>

    <editor-content class="editor__content" :editor="editor" />
    <input ref="filePicker" type="file" style="display: none" @change="onFilePicked" />
  </div>
</template>

<script>
/* eslint-disable no-console */
import "./menubar.scss";
import "./editor.scss";

import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  Blockquote,
  BulletList,
  CodeBlock,
  HardBreak,
  Heading,
  ListItem,
  OrderedList,
  TodoItem,
  TodoList,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image,
  Table,
  TableHeader,
  TableCell,
  TableRow,
} from "tiptap-extensions";

export default {
  components: {
    EditorContent,
    EditorMenuBar,
  },
  props: {
    value: {
      required: false,
    },
    editable: {
      required: false,
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      editor: new Editor({
        editable: this.editable,
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Image(),
          new Table({
            resizable: true,
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
        ],
        content: this.value,
        onUpdate: ({ getJSON }) => {
          this.$emit("input", getJSON());
        },
      }),
      command: null,
    };
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    showImagePrompt(command) {
      this.command = command;
      this.$refs.filePicker.click();
    },
    onFilePicked(e) {
      try {
        const { files } = e.target;

        let src = "";
        if (files[0] !== undefined) {
          const fileName = files[0].name;

          if (fileName.lastIndexOf(".") <= 0) {
            return;
          }
          const fr = new FileReader();
          fr.readAsDataURL(files[0]);
          fr.addEventListener("load", () => {
            src = fr.result;
            if (src !== null) {
              this.command({ src });
            }
            this.$refs.filePicker.value = "";
          });
        }
      } catch (error) {
        console.log(error);
      }
    },
    setContent(content) {
      this.editor.setContent(content);
    },
  },
  watch: {
    value(value) {
      if (!this.editable) {
        this.editor.setContent(value);
      }
    },
    editable(value) {
      this.editor.setOptions({ editable: value });
    },
  },
};
</script>
